// "Leitura da IA" genérica (headline + itens), visual do CampaignAiInsight / HeatmapAiInsight.
import React from 'react';
import { Box, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { __t } from '../../i18n/t';
import { InsightContent } from '../../models/attendantGoalOffer';
import { COLORS, shimmer } from '../../resources/dashboards/shared';

const AI_GRADIENT = 'linear-gradient(135deg, #6366f1, #a855f7)';

// kind de cada item por momento da IA (contrato seção 5): finding|goal|how, team|goal|follow_up,
// before_after|team|next_step, reason. Kind desconhecido cai no ícone de ação.
const ITEM_ICON: Record<string, { Icon: React.ElementType; color: string }> = {
  finding: { Icon: QueryStatsIcon, color: COLORS.blue },
  team: { Icon: QueryStatsIcon, color: COLORS.blue },
  before_after: { Icon: QueryStatsIcon, color: COLORS.blue },
  reason: { Icon: QueryStatsIcon, color: COLORS.blue },
  goal: { Icon: TrackChangesIcon, color: '#7c3aed' },
  how: { Icon: ArrowForwardIcon, color: COLORS.teal },
  follow_up: { Icon: ArrowForwardIcon, color: COLORS.teal },
  next_step: { Icon: ArrowForwardIcon, color: COLORS.teal },
};

const containerSx = {
  maxWidth: 920, display: 'flex', flexDirection: 'column' as const, gap: 1,
  borderRadius: 3, p: 1.75,
  background: 'linear-gradient(135deg, rgba(99,102,241,0.07), rgba(168,85,247,0.08))',
  border: '1px solid rgba(139,92,246,0.22)',
};

const Badge = () => (
  <Box sx={{ width: 22, height: 22, borderRadius: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: AI_GRADIENT }}>
    <AutoAwesomeIcon sx={{ fontSize: 13, color: '#fff' }} />
  </Box>
);

interface Props {
  content: InsightContent | null;
  loading: boolean;
  chip?: React.ReactNode;
  /** Texto fixo para quando não há leitura pronta (sem ele, o painel some). */
  message?: string;
  /** Título do painel com a leitura pronta (padrão: "Leitura da IA"). */
  title?: string;
  /** Texto do estado carregando (padrão: "A IA está lendo os dados da sua equipe…"). */
  loadingText?: string;
  sx?: Record<string, any>;
}

const AiInsightPanel: React.FC<Props> = ({ content, loading, chip, message, title, loadingText, sx }) => {
  if (loading) {
    return (
      <Box sx={{ ...containerSx, ...sx }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge />
          <Typography sx={{
            fontSize: 12.5, fontWeight: 600,
            background: 'linear-gradient(90deg, #a5b4fc 25%, #d8b4fe 50%, #a5b4fc 75%)',
            backgroundSize: '200% 100%', animation: `${shimmer} 1.4s ease-in-out infinite`,
            WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            {loadingText ?? __t('A IA está lendo os dados da sua equipe…')}
          </Typography>
        </Box>
      </Box>
    );
  }
  if (!content) {
    if (!message) return null;
    return (
      <Box sx={{ ...containerSx, ...sx }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge />
          <Typography sx={{ fontSize: 12.5, color: '#334155' }}>{message}</Typography>
        </Box>
      </Box>
    );
  }
  const items = (content.items ?? []).slice(0, 4);
  return (
    <Box sx={{ ...containerSx, ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Badge />
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, background: AI_GRADIENT, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          {title ?? __t('Leitura da IA')}
        </Typography>
        {chip}
      </Box>
      {/* Texto da IA sempre como texto puro, nunca dangerouslySetInnerHTML. */}
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: COLORS.ink }}>{content.headline}</Typography>
      {items.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {items.map((item, i) => {
            const { Icon, color } = ITEM_ICON[item.kind] ?? ITEM_ICON.how;
            return (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                <Icon sx={{ fontSize: 15, color, mt: 0.2, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12.5, color: '#334155' }}>{item.text}</Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default AiInsightPanel;
